"use client";
import { cn } from "@/lib/utils";
import { ResetIcon } from "@radix-ui/react-icons";
import { ChevronDown } from "lucide-react";
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ComboBox,
  type ComboBoxOptionType,
  DropdownInput,
  type DropdownOptionType,
  type TooltipProps,
} from ".";
import HexColorPicker from "./color-picker/HexColorPicker";
import type { NewPropertyState } from "@/components/RightStyle/usePropertyStateV2";
import {
  normalizeHexValue,
  parseColorValue,
} from "./color-picker/utils/colorFormatUtils";

interface ColorInputProps extends React.HTMLAttributes<HTMLDivElement> {
  propertyState: Partial<NewPropertyState> & {
    propertyValue: string;
    displayValue: string;
    isSet: boolean;
  };

  comboboxOptions: ComboBoxOptionType[];
  setValue: (option: ComboBoxOptionType) => void; // Called when interaction is complete
  instantUpdateValue: (option: ComboBoxOptionType) => void; // Called during dragging for real-time updates
  buildArbitraryClass: (input: string) => ComboBoxOptionType;

  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  onClear: () => void;
  showHex: boolean;
  onOpenVariantModal?: () => void;
  variantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onBeforeSetValue?: () => void;
}

const CustomColorPicker: React.FC<ColorInputProps & TooltipProps> = ({
  propertyState,
  comboboxOptions,
  setValue,
  instantUpdateValue,
  buildArbitraryClass,
  buildOptionPreview,
  onInputFocus,
  disabled = false,
  tooltipContent = null,
  onClear,
  dropdownOptions = [],
  onDropdownSelected,
  showHex = true,
  onOpenVariantModal,
  variantAnchorRef,
  onBeforeSetValue,
  ...restProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [comboboxOpen, setComboboxOpen] = useState<null | boolean>(false);
  const [focused, setFocused] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState<null | boolean>(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState(
    propertyState.displayValue || propertyState.propertyValue
  );
  const [isDragging, setIsDragging] = useState(false);
  const lastColorValueRef = useRef<string>("");
  const dragStartValueRef = useRef<string>("");

  React.useEffect(() => {
    setInputValue(propertyState.displayValue || propertyState.propertyValue);
  }, [propertyState.displayValue, propertyState.propertyValue]);

  // Use colorPickerValue for the swatch so it always matches the picker and hex field
  const swatchColor = propertyState.displayValue || propertyState.propertyValue;

  // On input change, update local state and call instantUpdateValue
  const onInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = ev.target.value;
      const normalizedValue = normalizeHexValue(rawValue);

      setInputValue(normalizedValue);
      if (instantUpdateValue && buildArbitraryClass) {
        instantUpdateValue(buildArbitraryClass(normalizedValue || ""));
      }
    },
    [instantUpdateValue, buildArbitraryClass]
  );

  // On blur or Enter, persist the custom value as a custom class
  const handleInputCommit = React.useCallback(() => {
    const normalizedValue = normalizeHexValue(inputValue || "");
    setInputValue(normalizedValue);

    if (setValue && buildArbitraryClass) {
      setValue(buildArbitraryClass(normalizedValue));
    }
    setComboboxOpen(false);
  }, [inputValue, setValue, buildArbitraryClass]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      if (inputValue === "" && onClear) {
        setComboboxOpen(false);
        onClear();
        if (inputRef.current) {
          inputRef.current.blur();
        }
      } else {
        handleInputCommit();
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    }
  };

  const handleDropdownOptionClick = (option: DropdownOptionType) => {
    if (onBeforeSetValue) onBeforeSetValue();
    if (option.value === "clear" && onClear) {
      onClear();
    } else if (onDropdownSelected) {
      onDropdownSelected(option);
    }
    setDropdownVisible(false);
  };

  const enhancedDropdownOptions = useMemo(() => {
    if (propertyState.isSet) {
      const clearDropdownOption: DropdownOptionType = {
        label: "Clear style",
        value: "clear",
        icon: <ResetIcon />,
      };
      return [...dropdownOptions, clearDropdownOption];
    }
    return dropdownOptions;
  }, [dropdownOptions, propertyState.isSet]);

  const colorPickerValue = useMemo(() => {
    const parsedColor = parseColorValue(
      propertyState.propertyValue,
      comboboxOptions
    );
    return parsedColor.hex;
  }, [propertyState.propertyValue, comboboxOptions]);

  // drag tracking for when the mouse ends up over an iframe
  React.useEffect(() => {
    if (!isDragging) return;

    const handleEnd = () => {
      if (lastColorValueRef.current !== dragStartValueRef.current) {
        setValue(buildArbitraryClass(lastColorValueRef.current));
      }
      setIsDragging(false);
      dragStartValueRef.current = "";
    };

    // Use a slight delay to ensure we don't interfere with the color picker's events
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, setValue, buildArbitraryClass]);

  // Global flag to track if a dropdown selection is in progress
  let isComboBoxDropdownClick = false;

  // On dropdown select from ComboBox, set the value and update inputValue
  const handleComboboxOptionClick = (option: ComboBoxOptionType) => {
    if (onBeforeSetValue) onBeforeSetValue();
    setValue({ ...option, css: option.css });
    setInputValue(option.tailwind || option.css || "");
    setComboboxOpen(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <div className="w-full" {...restProps}>
      <HexColorPicker
        id="hex-color-picker"
        color={colorPickerValue}
        onChange={(color) => {
          const normalizedColor = normalizeHexValue(color);
          setInputValue(normalizedColor);
          lastColorValueRef.current = normalizedColor;

          // Always call instantUpdate for real-time feedback
          const arbitraryClass = buildArbitraryClass(normalizedColor);
          instantUpdateValue(arbitraryClass);

          // Start drag tracking on first change
          if (!isDragging) {
            dragStartValueRef.current = colorPickerValue;
            setIsDragging(true);
          }
        }}
        onPointerDown={restProps.onPointerDown}
      />
      {showHex === true && (
        <div ref={colorInputRef}>
          <div
            className="flex justify-between items-center mb-2"
            data-tooltip-id="tooltip"
            data-tooltip-content={tooltipContent}
            data-tooltip-place="bottom"
            data-tooltip-offset={5}
            data-tooltip-hidden={Boolean(comboboxOpen || dropdownVisible)}
            onMouseDown={(event) => {
              if (event.button === 2) {
                // Right click
                event.preventDefault();
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              if (
                enhancedDropdownOptions &&
                enhancedDropdownOptions.length > 0
              ) {
                setDropdownVisible(true);
              }
            }}
          >
            <div className="text-xs text-secondary-800 font-medium">Hex</div>
            <div
              className={cn(
                "flex items-center gap-1 w-full max-w-[140px] h-7 bg-secondary-n-950 rounded-md relative",
                focused && "border border-primary-400"
              )}
            >
              <button
                type="button"
                className="ml-1 w-3 h-3 rounded-xs shadow-inner cursor-pointer absolute"
                style={{ backgroundColor: swatchColor }}
              />
              <input
                ref={inputRef}
                disabled={disabled}
                value={inputValue}
                onChange={onInputChange}
                onFocus={(event) => {
                  event.target.select();
                  setComboboxOpen(true);
                  setFocused(true);
                  if (onInputFocus) {
                    onInputFocus(event);
                  }
                }}
                onKeyDown={handleKeyDown}
                onBlur={(event) => {
                  setFocused(false);
                  if (isComboBoxDropdownClick) {
                    // If blur was caused by clicking the dropdown, skip commit
                    return;
                  }
                  handleInputCommit();
                }}
                className={`text-2xs pl-5 pr-8 border-none outline-none font-medium w-full h-full rounded-md bg-secondary-n-950 ${
                  disabled || !propertyState.isSet
                    ? "text-secondary-600"
                    : "text-white"
                }`}
              />
              <button
                type="button"
                onClick={
                  onOpenVariantModal
                    ? onOpenVariantModal
                    : () => setDropdownVisible(true)
                }
                className="absolute right-1"
                ref={variantAnchorRef}
              >
                <ChevronDown className="text-secondary-n-500 h-4 w-4" />
              </button>
              <DropdownInput
                className="w-40"
                dropdownOptions={enhancedDropdownOptions}
                onDropdownSelected={handleDropdownOptionClick}
                visible={Boolean(dropdownVisible)}
                setVisible={setDropdownVisible}
              />
            </div>
            {comboboxOpen && comboboxOptions && (
              <ComboBox
                input={inputValue || ""}
                options={comboboxOptions}
                open={comboboxOpen}
                closeCombobox={() => setComboboxOpen(false)}
                onOptionSelected={(option: ComboBoxOptionType) =>
                  handleComboboxOptionClick(option)
                }
                buildOptionPreview={buildOptionPreview}
                buildArbitraryClass={buildArbitraryClass}
                anchorRef={colorInputRef}
                onMouseDown={() => {
                  isComboBoxDropdownClick = true;
                }}
                onMouseUp={() => {
                  setTimeout(() => {
                    isComboBoxDropdownClick = false;
                  }, 0);
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomColorPicker);
